import Quill from 'quill'

const SizeStyle = Quill.import('attributors/style/size')
SizeStyle.whitelist = ['8px', '9px', '10px', '11px', '12px', '13px', '14px', '15px', '16px', '18px']
Quill.register(SizeStyle, true)

const FontStyle = Quill.import('attributors/style/font')
FontStyle.whitelist = ['Calibri', 'Arial', 'Georgia', 'Times New Roman']
Quill.register(FontStyle, true)

const BlockEmbed = Quill.import('blots/block/embed')

class OfferManualPageBreakBlot extends BlockEmbed {
  static create() {
    const node = super.create()
    node.setAttribute('contenteditable', 'false')
    node.setAttribute('aria-hidden', 'true')
    node.innerHTML = '<hr /><span>Page break</span>'
    return node
  }

  static value() {
    return true
  }
}

OfferManualPageBreakBlot.blotName = 'offerPageBreak'
OfferManualPageBreakBlot.tagName = 'DIV'
OfferManualPageBreakBlot.className = 'offer-manual-page-break'
Quill.register(OfferManualPageBreakBlot)

const Icons = Quill.import('ui/icons')
Icons.offerPageBreak = [
  '<svg viewbox="0 0 18 18" aria-hidden="true">',
  '<line class="ql-stroke" x1="2" x2="16" y1="9" y2="9"></line>',
  '<polyline class="ql-stroke" points="4 5 2 8 4 11" fill="none"></polyline>',
  '<polyline class="ql-stroke" points="14 5 16 8 14 11" fill="none"></polyline>',
  '</svg>',
].join('')

const OFFER_QUILL_TOOLBAR_GROUP_LABELS = [
  'Font',
  'Size',
  'Style',
  'Lists',
  'Alignment',
  'Page break',
]

const OFFER_QUILL_TOOLBAR_TITLES = [
  ['button.ql-bold', 'Bold'],
  ['button.ql-italic', 'Italic'],
  ['button.ql-underline', 'Underline'],
  ['button.ql-list[value="ordered"]', 'Numbered list'],
  ['button.ql-list[value="bullet"]', 'Bullet list'],
  ['button.ql-align[value=""]', 'Align left'],
  ['button.ql-align[value="center"]', 'Align center'],
  ['button.ql-align[value="right"]', 'Align right'],
  ['button.ql-align[value="justify"]', 'Justify'],
  ['button.ql-offerPageBreak', 'Insert page break at cursor'],
  ['.ql-picker.ql-font', 'Font family'],
  ['.ql-picker.ql-size', 'Text size'],
]

/** Quill toolbar `<option>` nodes have values but no text — copy into data-label for picker CSS. */
function applyOfferQuillPickerLabels(toolbar) {
  toolbar.querySelectorAll('.ql-picker.ql-font .ql-picker-item, .ql-picker.ql-size .ql-picker-item').forEach(el => {
    const value = el.getAttribute('data-value')
    if (value) {
      el.setAttribute('data-label', value)
      return
    }
    if (el.classList.contains('ql-picker-item') && el.closest('.ql-font')) {
      el.setAttribute('data-label', 'Calibri')
    }
    if (el.classList.contains('ql-picker-item') && el.closest('.ql-size')) {
      el.setAttribute('data-label', '12px')
    }
  })

  toolbar.querySelectorAll('.ql-picker.ql-font .ql-picker-label, .ql-picker.ql-size .ql-picker-label').forEach(el => {
    const value = el.getAttribute('data-value')
    if (value) {
      el.setAttribute('data-label', value)
    } else if (el.closest('.ql-font')) {
      el.setAttribute('data-label', 'Calibri')
    } else if (el.closest('.ql-size')) {
      el.setAttribute('data-label', '12px')
    }
  })
}

/** Add HR-friendly labels and tooltips to a Quill Snow toolbar. */
export function applyOfferQuillToolbarLabels(editorRoot) {
  if (!editorRoot || typeof editorRoot.querySelector !== 'function') return
  const toolbar = editorRoot.classList.contains('ql-toolbar')
    ? editorRoot
    : editorRoot.querySelector('.ql-toolbar')
  if (!toolbar) return

  OFFER_QUILL_TOOLBAR_TITLES.forEach(([selector, title]) => {
    toolbar.querySelectorAll(selector).forEach(el => {
      el.setAttribute('title', title)
      el.setAttribute('aria-label', title)
    })
  })

  toolbar.querySelectorAll('.ql-formats').forEach((group, idx) => {
    const label = OFFER_QUILL_TOOLBAR_GROUP_LABELS[idx]
    if (label) group.setAttribute('data-group-label', label)
  })

  applyOfferQuillPickerLabels(toolbar)
}

const OFFER_QUILL_TOOLBAR_ITEMS = [
  [{ font: ['Calibri', 'Arial', 'Georgia', 'Times New Roman'] }],
  [{ size: ['8px', '9px', '10px', '11px', '12px', '13px', '14px', '15px', '16px', '18px'] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
  ['offerPageBreak'],
]

const OFFER_QUILL_PAGE_BREAK_HANDLER = {
  offerPageBreak() {
    const quill = this.quill
    const range = quill.getSelection(true)
    const index = range ? range.index : Math.max(0, quill.getLength() - 1)
    quill.insertEmbed(index, 'offerPageBreak', true, 'user')
    quill.insertText(index + 1, '\n', 'user')
    quill.setSelection(index + 2, 0, 'user')
  },
}

/** Snow theme options with the standard offer letter toolbar (rendered inline, then moved to an external slot). */
export function buildOfferQuillOptions() {
  return {
    theme: 'snow',
    modules: {
      toolbar: {
        container: OFFER_QUILL_TOOLBAR_ITEMS,
        handlers: OFFER_QUILL_PAGE_BREAK_HANDLER,
      },
      clipboard: {
        matchers: [
          ['div.offer-manual-page-break', () => {
            const Delta = Quill.import('delta')
            return new Delta().insert({ offerPageBreak: true })
          }],
        ],
      },
    },
  }
}

const OFFER_QUILL_OPTIONS = buildOfferQuillOptions()

/** Cached options object — safe to reuse across editors (relocation is per-instance). */
export function getOfferQuillOptions() {
  return OFFER_QUILL_OPTIONS
}

/**
 * Move Quill's inline `.ql-toolbar` into an external slot (right column).
 * Quill only auto-builds controls when `container` is an array; a CSS selector
 * expects pre-existing buttons, so an empty slot div stays blank.
 */
export function mountOfferQuillExternalToolbar(editorRoot, toolbarSlot) {
  if (!editorRoot || !toolbarSlot) return null
  const toolbar = editorRoot.querySelector('.ql-toolbar')
  if (!toolbar) return null

  toolbarSlot.querySelectorAll('.ql-toolbar').forEach(el => {
    if (el !== toolbar) el.remove()
  })

  if (toolbar.parentElement !== toolbarSlot) {
    toolbarSlot.appendChild(toolbar)
  }

  applyOfferQuillToolbarLabels(toolbar)
  return toolbar
}
